import {
  getBlockBackgroundColor,
  getBlockTextColor,
} from '@flowus/common/block/color/get-block-color';
import { ColorKey } from 'src/colors';
import type { TFunction } from 'i18next';

/**
 * Helper function to create color picker data with translations
 * This function should be called from components that have access to the translation hook
 */
export const createColorPickerData = (t: TFunction) => {
  /** 文字颜色 */
  const COLOR_PICKER_DATA_TEXT_COLOR = {
    title: t('components.color_picker.text_color'),
    items: [
      {
        color: getBlockBackgroundColor(ColorKey.white),
        desc: t('common.default'),
        isBgColor: true,
        colorkey: '',
      },
      {
        color: getBlockTextColor(ColorKey.black),
        desc: t('components.color_picker.black'),
        colorkey: ColorKey.black,
      },
      {
        color: getBlockTextColor(ColorKey.grey),
        desc: t('common.grey'),
        colorkey: ColorKey.grey,
      },
      {
        color: getBlockTextColor(ColorKey.brown),
        desc: t('common.brown'),
        colorkey: ColorKey.brown,
      },
      {
        color: getBlockTextColor(ColorKey.orange),
        desc: t('components.color_picker.orange'),
        colorkey: ColorKey.orange,
      },
      {
        color: getBlockTextColor(ColorKey.pink),
        desc: t('common.pink'),
        colorkey: ColorKey.pink,
      },
      {
        color: getBlockTextColor(ColorKey.yellow),
        desc: t('common.yellow'),
        colorkey: ColorKey.yellow,
      },
      {
        color: getBlockTextColor(ColorKey.green),
        desc: t('common.green'),
        colorkey: ColorKey.green,
      },
      {
        color: getBlockTextColor(ColorKey.blue),
        desc: t('common.blue'),
        colorkey: ColorKey.blue,
      },
      {
        color: getBlockTextColor(ColorKey.purple),
        desc: t('common.purple'),
        colorkey: ColorKey.purple,
      },
      {
        color: getBlockTextColor(ColorKey.red),
        desc: t('common.red'),
        colorkey: ColorKey.red,
      },
    ],
  };

  /** 背景颜色 */
  const COLOR_PICKER_DATA_BACKGROUND_COLOR = {
    title: t('components.color_picker.background_color'),
    items: [
      {
        color: getBlockBackgroundColor(ColorKey.white),
        desc: t('common.default'),
        isBgColor: true,
        colorkey: '',
      },
      {
        color: getBlockBackgroundColor(ColorKey.grey),
        desc: t('common.grey'),
        isBgColor: true,
        colorkey: ColorKey.grey,
      },
      {
        color: getBlockBackgroundColor(ColorKey.brown),
        desc: t('common.brown'),
        isBgColor: true,
        colorkey: ColorKey.brown,
      },
      {
        color: getBlockBackgroundColor(ColorKey.orange),
        desc: t('components.color_picker.orange'),
        isBgColor: true,
        colorkey: ColorKey.orange,
      },
      {
        color: getBlockBackgroundColor(ColorKey.pink),
        desc: t('common.pink'),
        isBgColor: true,
        colorkey: ColorKey.pink,
      },
      {
        color: getBlockBackgroundColor(ColorKey.yellow),
        desc: t('common.yellow'),
        isBgColor: true,
        colorkey: ColorKey.yellow,
      },
      {
        color: getBlockBackgroundColor(ColorKey.green),
        desc: t('common.green'),
        isBgColor: true,
        colorkey: ColorKey.green,
      },
      {
        color: getBlockBackgroundColor(ColorKey.blue),
        desc: t('common.blue'),
        isBgColor: true,
        colorkey: ColorKey.blue,
      },
      {
        color: getBlockBackgroundColor(ColorKey.purple),
        desc: t('common.purple'),
        isBgColor: true,
        colorkey: ColorKey.purple,
      },
      {
        color: getBlockBackgroundColor(ColorKey.red),
        desc: t('common.red'),
        isBgColor: true,
        colorkey: ColorKey.red,
      },
    ],
  };

  /** 渐变背景 */
  const GRADIENT_COLOR = [
    {
      color: getBlockBackgroundColor(ColorKey.yellow_green),
      desc: t('components.color_picker.gradient_yellow_green'),
      isBgColor: true,
      colorkey: ColorKey.yellow_green,
    },
    {
      color: getBlockBackgroundColor(ColorKey.green_blue),
      desc: t('components.color_picker.gradient_green_blue'),
      isBgColor: true,
      colorkey: ColorKey.green_blue,
    },
    {
      color: getBlockBackgroundColor(ColorKey.blue_purple),
      desc: t('components.color_picker.gradient_blue_purple'),
      isBgColor: true,
      colorkey: ColorKey.blue_purple,
    },
    {
      color: getBlockBackgroundColor(ColorKey.purple_red),
      desc: t('components.color_picker.gradient_purple_red'),
      isBgColor: true,
      colorkey: ColorKey.purple_red,
    },
    {
      color: getBlockBackgroundColor(ColorKey.red_orange),
      desc: t('components.color_picker.gradient_red_orange'),
      isBgColor: true,
      colorkey: ColorKey.red_orange,
    },
  ];

  /** 荧光背景色 */
  const HAS_LIGHT_BACKGROUND_COLOR = [
    {
      color: getBlockBackgroundColor(ColorKey.light_yellow),
      desc: t('components.color_picker.light_yellow'),
      isBgColor: true,
      colorkey: ColorKey.light_yellow,
    },
    {
      color: getBlockBackgroundColor(ColorKey.light_green),
      desc: t('components.color_picker.light_green'),
      isBgColor: true,
      colorkey: ColorKey.light_green,
    },
    {
      color: getBlockBackgroundColor(ColorKey.light_blue),
      desc: t('components.color_picker.light_blue'),
      isBgColor: true,
      colorkey: ColorKey.light_blue,
    },
    {
      color: getBlockBackgroundColor(ColorKey.light_purple),
      desc: t('components.color_picker.light_purple'),
      isBgColor: true,
      colorkey: ColorKey.light_purple,
    },
    {
      color: getBlockBackgroundColor(ColorKey.light_red),
      desc: t('components.color_picker.light_red'),
      isBgColor: true,
      colorkey: ColorKey.light_red,
    },
  ];

  /** 基础色 + 荧光色 */
  const COLOR_PICKER_DATA_HAS_LIGHT_BACKGROUND_COLOR = {
    ...COLOR_PICKER_DATA_BACKGROUND_COLOR,
    items: [...COLOR_PICKER_DATA_BACKGROUND_COLOR.items, ...HAS_LIGHT_BACKGROUND_COLOR],
  };

  const COLOR_PICKER_DATA_HAS_LIGHT_AND_GRADIENT = {
    ...COLOR_PICKER_DATA_BACKGROUND_COLOR,
    items: [
      ...COLOR_PICKER_DATA_BACKGROUND_COLOR.items,
      ...HAS_LIGHT_BACKGROUND_COLOR,
      ...GRADIENT_COLOR,
    ],
  };

  const DEFAULT_COLOR_PICKER_DATA_NOT_LIGHT = [
    COLOR_PICKER_DATA_TEXT_COLOR,
    COLOR_PICKER_DATA_BACKGROUND_COLOR,
  ];

  /**
   * 颜色值以figma 桌面端页面右侧的颜色为主
   */
  const DEFAULT_COLOR_PICKER_DATA = [
    COLOR_PICKER_DATA_TEXT_COLOR,
    COLOR_PICKER_DATA_HAS_LIGHT_BACKGROUND_COLOR,
  ];

  /** 行内文字颜色 */
  const INLINE_COLOR_PICKER_DATA_TEXT_COLOR = [
    COLOR_PICKER_DATA_TEXT_COLOR,
    COLOR_PICKER_DATA_HAS_LIGHT_AND_GRADIENT,
  ];

  return {
    COLOR_PICKER_DATA_TEXT_COLOR,
    COLOR_PICKER_DATA_BACKGROUND_COLOR,
    DEFAULT_COLOR_PICKER_DATA_NOT_LIGHT,
    DEFAULT_COLOR_PICKER_DATA,
    INLINE_COLOR_PICKER_DATA_TEXT_COLOR,
  };
};

/**
 * Legacy exports for backward compatibility
 * These will be populated with English defaults when imported directly
 * Components should use createColorPickerData(t) for proper i18n support
 */
const createDefaultData = () => {
  const defaultT = (key: string) => {
    const translations: Record<string, string> = {
      'components.color_picker.text_color': 'Text Color',
      'components.color_picker.background_color': 'Background Color',
      'components.color_picker.default': 'Default',
      'components.color_picker.black': 'Black',
      'components.color_picker.grey': 'Grey',
      'components.color_picker.brown': 'Brown',
      'components.color_picker.orange': 'Orange',
      'components.color_picker.pink': 'Pink',
      'components.color_picker.yellow': 'Yellow',
      'components.color_picker.green': 'Green',
      'components.color_picker.blue': 'Blue',
      'components.color_picker.purple': 'Purple',
      'components.color_picker.red': 'Red',
      'components.color_picker.gradient_yellow_green': 'Yellow to Green',
      'components.color_picker.gradient_green_blue': 'Green to Blue',
      'components.color_picker.gradient_blue_purple': 'Blue to Purple',
      'components.color_picker.gradient_purple_red': 'Purple to Red',
      'components.color_picker.gradient_red_orange': 'Red to Orange',
      'components.color_picker.light_yellow': 'Neon Yellow',
      'components.color_picker.light_green': 'Neon Green',
      'components.color_picker.light_blue': 'Neon Blue',
      'components.color_picker.light_purple': 'Neon Purple',
      'components.color_picker.light_red': 'Neon Red',
    };
    return translations[key] || key;
  };
  return createColorPickerData(defaultT as unknown as TFunction);
};

const defaultData = createDefaultData();

export const COLOR_PICKER_DATA_TEXT_COLOR = defaultData.COLOR_PICKER_DATA_TEXT_COLOR;
export const COLOR_PICKER_DATA_BACKGROUND_COLOR = defaultData.COLOR_PICKER_DATA_BACKGROUND_COLOR;
export const DEFAULT_COLOR_PICKER_DATA_NOT_LIGHT = defaultData.DEFAULT_COLOR_PICKER_DATA_NOT_LIGHT;
export const DEFAULT_COLOR_PICKER_DATA = defaultData.DEFAULT_COLOR_PICKER_DATA;
export const INLINE_COLOR_PICKER_DATA_TEXT_COLOR = defaultData.INLINE_COLOR_PICKER_DATA_TEXT_COLOR;
